import { tl } from 'common/services/i18nService';
import { AWSRumCustomEvents } from 'common/store/common/constants';
import { SessionState } from 'common/store/session/reducer';
import { StoreManager } from 'common/store/typings';
import { setWorkspaceOverlay } from 'common/store/workspaces/actions';
import { WorkspacesOverlayTargets, WorkspacesOverlayTypes } from 'common/store/workspaces/types';
import { recordCustomEvent } from 'common/util/awsRumUtils';
import itemService from '../../services/itemService';
import { ContextState } from '../../store/context/reducer';
import { requestEditorContentSave } from '../../features/contentEditor/actions';
import { QUESTION_SET_SUMMARY_COLUMNS } from '../../features/questionSetSummary/constants';
import { getQuestionSetSummaryConfig } from '../../features/questionSetSummary/config';

const CONTENT_EDITOR_FEATURE_IDS = [
    'feature_contentEditor_author_edit_2_0',
    'feature_contentEditor_author_question_edit_only_with_editor_2_0',
];

const getContentEditorFeatureId = (state: any): string | undefined => {
    return CONTENT_EDITOR_FEATURE_IDS.find((featureId) => !!state[featureId]?.iframeTarget);
};

const SAVE_BEFORE_PM_TIMEOUT_MS = 10000;

export const openPrintManger = (store: StoreManager, session: SessionState) => {
    const workspaceOverlayName = WorkspacesOverlayTypes.DOWNLOAD_AND_PRINT;
    const workspaceTitle = tl('common.overlay.printAndDownload.title');
    const options = {
        hideTitle: true,
        styleOptionsTemplate: session.marketplaceProperties.ENABLE_PRINT_MANAGER_PB_VERSION ? 'paperBased' : undefined,
        context: 'preview',
    };

    const openOverlay = () => {
        store.dispatch(
            setWorkspaceOverlay(
                workspaceOverlayName,
                WorkspacesOverlayTargets.APP_OVERLAY_TARGET,
                {
                    workspaceTitle,
                    options,
                }
            )
        );
        recordCustomEvent(AWSRumCustomEvents.OPEN_PRINT_VIEW, 'Print view was clicked');
        const params = new URLSearchParams(window.location.search);
        params.set('printMode', 'true');
        const search = params.toString();
        const queryString = search ? `?${search}` : '';
        const nextUrl = `${window.location.pathname}${queryString}${window.location.hash}`;
        window.history.replaceState({}, '', nextUrl);
    };

    const currentState = store.getState() as any;
    const { isDirtyCurrentQtiContextItem, isLocked } = currentState.context;

    // A locked (e.g. Live) item is read-only, so it cannot have legitimate unsaved
    // changes: a dirty flag in that state is a false positive raised while the
    // legacy editor is still initializing. Requesting a save there would run the
    // question validation against a half-initialized model (surfacing bogus
    // "must have scoring set" errors), so open the Print Manager directly.
    if (isDirtyCurrentQtiContextItem && !isLocked) {
        const featureId = getContentEditorFeatureId(currentState);
        if (featureId) {
            let done = false;
            let unsubscribe: () => void;

            const finishAndOpen = () => {
                if (!done) {
                    done = true;
                    clearTimeout(timeoutId);
                    if (unsubscribe) { unsubscribe(); }
                    openOverlay();
                }
            };

            const timeoutId = setTimeout(finishAndOpen, SAVE_BEFORE_PM_TIMEOUT_MS);

            unsubscribe = store.getStore().subscribe(() => {
                const newState = store.getState() as any;
                if (!newState.context.isDirtyCurrentQtiContextItem) {
                    finishAndOpen();
                }
            });

            // the 2nd argument is true to suggest that we are in print view now
            store.dispatch(requestEditorContentSave(featureId, true));

            if (!(store.getState() as any).context.isDirtyCurrentQtiContextItem) {
                finishAndOpen();
            }

            return;
        }
    }

    openOverlay();
};

export const openLogEvents = (context: ContextState) => {
    const url: string = itemService.getLogEventsUrl(context);
    window.open(url, '_blank');
};

export const openReviewTool = (context: ContextState) => {
    const url: string = itemService.getItemReviewUrl(context);
    window.open(url, '_blank');
};

export const openQuestionSetSummary = (store: StoreManager, context: ContextState) => {
    const questionSetSummaryConfig = getQuestionSetSummaryConfig();
    const isQuestionSetSummaryEnabled = questionSetSummaryConfig?.enableFeature === true;
    const isQuestionSetSummaryDownloadEnabled = questionSetSummaryConfig?.download === true;

    if (!isQuestionSetSummaryEnabled) {
        return;
    }

    const workspaceOverlayName = WorkspacesOverlayTypes.QUESTION_SET_SUMMARY;
    const workspaceTitle = tl('author.features.QSSummary.title');
    const options = {
        withoutHeader: true,
        columns: QUESTION_SET_SUMMARY_COLUMNS,
        assessmentId: context?.currentItemContext?.objectId,
        revisionId: context?.currentItemContext?.liveContentRevisionId,
        showDownload: isQuestionSetSummaryDownloadEnabled,
    };

    store.dispatch(
        setWorkspaceOverlay(
            workspaceOverlayName,
            WorkspacesOverlayTargets.APP_OVERLAY_TARGET,
            {
                workspaceTitle,
                options,
            }
        )
    );
};
